import React, { useState, useEffect } from "react";
import {
  Cookie,
  Settings,
  Check,
  X,
  Info,
  Shield,
  BarChart3,
  Zap,
} from "lucide-react";
import "./CookieConsent.css";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always true, cannot be disabled
    functional: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(consent);
        setCookiePreferences((prev) => ({ ...prev, ...savedPreferences }));
        applyCookieSettings(savedPreferences);
      } catch (error) {
        console.error("Error parsing cookie preferences:", error);
      }
    }
  }, []);

  const applyCookieSettings = (preferences) => {
    // Apply cookie settings based on user preferences
    if (preferences.analytics) {
      // Enable analytics cookies (Google Analytics, etc.)
      console.log("Analytics cookies enabled");
      // Example: gtag('consent', 'update', { analytics_storage: 'granted' });
    } else {
      console.log("Analytics cookies disabled");
      // Example: gtag('consent', 'update', { analytics_storage: 'denied' });
    }

    if (preferences.marketing) {
      // Enable marketing cookies
      console.log("Marketing cookies enabled");
      // Example: gtag('consent', 'update', { ad_storage: 'granted' });
    } else {
      console.log("Marketing cookies disabled");
      // Example: gtag('consent', 'update', { ad_storage: 'denied' });
    }

    if (preferences.functional) {
      // Enable functional cookies
      console.log("Functional cookies enabled");
    } else {
      console.log("Functional cookies disabled");
    }

    // Essential cookies are always enabled
    console.log("Essential cookies enabled (always on)");
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };

    setCookiePreferences(allAccepted);
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    applyCookieSettings(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };

    setCookiePreferences(onlyEssential);
    localStorage.setItem("cookieConsent", JSON.stringify(onlyEssential));
    applyCookieSettings(onlyEssential);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(cookiePreferences));
    applyCookieSettings(cookiePreferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === "essential") return; // Essential cookies cannot be disabled

    setCookiePreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const cookieTypes = [
    {
      id: "essential",
      name: "Essential Cookies",
      description:
        "Required for the website to function properly. These cannot be disabled.",
      icon: Shield,
      color: "#10b981",
      required: true,
    },
    {
      id: "functional",
      name: "Functional Cookies",
      description: "Remember your preferences and provide enhanced features.",
      icon: Zap,
      color: "#3b82f6",
      required: false,
    },
    {
      id: "analytics",
      name: "Analytics Cookies",
      description:
        "Help us understand how you use our website to improve your experience.",
      icon: BarChart3,
      color: "#f59e0b",
      required: false,
    },
    {
      id: "marketing",
      name: "Marketing Cookies",
      description:
        "Used to deliver personalized advertisements and track campaign effectiveness.",
      icon: Cookie,
      color: "#8b5cf6",
      required: false,
    },
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      {showSettings && (
        <div
          className="cookie-overlay"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Cookie Banner */}
      <div className={`cookie-banner ${showSettings ? "with-settings" : ""}`}>
        <div className="cookie-content">
          {!showSettings ? (
            // Main Banner
            <div className="banner-main">
              <div className="banner-info">
                <div className="banner-icon">
                  <Cookie size={24} />
                </div>
                <div className="banner-text">
                  <h3>We use cookies</h3>
                  <p>
                    We use cookies to enhance your browsing experience, serve
                    personalized content, and analyze our traffic. By clicking
                    "Accept All", you consent to our use of cookies.
                  </p>
                  <div className="banner-links">
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      Terms & Conditions
                    </a>
                  </div>
                </div>
              </div>

              <div className="banner-actions">
                <button
                  className="btn btn-settings"
                  onClick={() => setShowSettings(true)}
                  aria-label="Cookie settings"
                >
                  <Settings size={16} />
                  Settings
                </button>
                <button className="btn btn-reject" onClick={handleRejectAll}>
                  Reject All
                </button>
                <button className="btn btn-accept" onClick={handleAcceptAll}>
                  <Check size={16} />
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="settings-panel">
              <div className="settings-header">
                <h3>Cookie Preferences</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowSettings(false)}
                  aria-label="Close settings"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="settings-content">
                <p className="settings-description">
                  Choose which cookies you want to accept. You can change these
                  settings at any time.
                </p>

                <div className="cookie-types">
                  {cookieTypes.map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <div key={type.id} className="cookie-type">
                        <div className="cookie-type-header">
                          <div className="cookie-type-info">
                            <div
                              className="cookie-type-icon"
                              style={{
                                backgroundColor: `${type.color}20`,
                                color: type.color,
                              }}
                            >
                              <IconComponent size={20} />
                            </div>
                            <div className="cookie-type-text">
                              <h4>{type.name}</h4>
                              <p>{type.description}</p>
                            </div>
                          </div>

                          <label className="cookie-toggle">
                            <input
                              type="checkbox"
                              checked={cookiePreferences[type.id]}
                              onChange={() => handlePreferenceChange(type.id)}
                              disabled={type.required}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>

                        {type.required && (
                          <div className="required-notice">
                            <Info size={14} />
                            <span>Always active</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="settings-actions">
                <button className="btn btn-reject" onClick={handleRejectAll}>
                  Reject All
                </button>
                <button className="btn btn-accept" onClick={handleAcceptAll}>
                  Accept All
                </button>
                <button
                  className="btn btn-save"
                  onClick={handleSavePreferences}
                >
                  <Check size={16} />
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
